import React, { useState, useEffect } from 'react';
import {
  Box,
  Container,
  Typography,
  Paper,
  Grid,
  Card,
  CardContent,
  Button,
  alpha,
  createTheme,
  useTheme as useMuiTheme,
  Breadcrumbs,
  Link,
  Divider,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Stack,
  Chip,
  IconButton,
  Tooltip,
  TextField,
  Alert,
  CircularProgress,
  Tabs,
  Tab
} from '@mui/material';
import {
  Assessment as AssessmentIcon,
  TrendingUp as TrendingUpIcon,
  People as PeopleIcon,
  AccessTime as AccessTimeIcon,
  Print as PrintIcon,
  FileDownload as FileDownloadIcon,
  Refresh as RefreshIcon,
  DateRange as DateRangeIcon,
  NavigateNext as NavigateNextIcon,
  Home as HomeIcon,
  Schedule as ScheduleIcon,
  Speed as SpeedIcon,
  Group as GroupIcon,
  Business as BusinessIcon
} from '@mui/icons-material';
import dayjs from 'dayjs';
import 'dayjs/locale/pt-br';
import 'dayjs/plugin/weekday';
import 'dayjs/plugin/isoWeek';
import 'dayjs/plugin/customParseFormat';
import 'dayjs/plugin/localizedFormat';
import 'dayjs/plugin/relativeTime';
import 'dayjs/plugin/duration';
import * as XLSX from 'xlsx';

import Header from '../components/Header';
import Sidebar from '../components/Sidebar';
import { useTheme } from '../context/ThemeContext';
import useSidebar from '../hooks/useSidebar';
import { useAuth } from '../context/AuthContext';

// Importar componentes de relatórios
import RelatorioGeral from '../components/relatorios/RelatorioGeral';
import RelatorioServicos from '../components/relatorios/RelatorioServicos';
import RelatorioTempoEspera from '../components/relatorios/RelatorioTempoEspera';
import TabelaDetalhada from '../components/relatorios/TabelaDetalhada';
import MetricasCard from '../components/relatorios/MetricasCard';
import RelatorioAtendentes from '../components/relatorios/RelatorioAtendentes';
import TabelaDesempenhoUsuarios from '../components/relatorios/TabelaDesempenhoUsuarios';
import TabelaSenhasDetalhadas from '../components/relatorios/TabelaSenhasDetalhadas';
import RelatorioServicosExecutados from '../components/relatorios/RelatorioServicosExecutados';

// Importar API de relatórios
import relatoriosApi from '../services/relatoriosApi';

// Importar o gerador de PDF
import PDFGenerator from '../components/relatorios/PDFGenerator';

// Configurar dayjs para português
dayjs.locale('pt-br');

const Relatorios = () => {
  const { sidebarOpen, toggleSidebar } = useSidebar();
  const { isDarkMode } = useTheme();
  const { user } = useAuth();
  const baseTheme = useMuiTheme();
  
  // Estados para filtros
  const [dataInicio, setDataInicio] = useState(dayjs().subtract(7, 'day'));
  const [dataFim, setDataFim] = useState(dayjs());
  const [servicosSelecionados, setServicosSelecionados] = useState([]);
  const [tipoRelatorio, setTipoRelatorio] = useState('geral');
  
  // Estados para o sistema de abas
  const [activeTab, setActiveTab] = useState(0);
  const [dadosUsuariosDetalhados, setDadosUsuariosDetalhados] = useState([]);
  const [carregandoUsuariosDetalhados, setCarregandoUsuariosDetalhados] = useState(false);
  const [dadosSenhasDetalhadas, setDadosSenhasDetalhadas] = useState([]);
  const [carregandoSenhasDetalhadas, setCarregandoSenhasDetalhadas] = useState(false);
  const [dadosServicosExecutados, setDadosServicosExecutados] = useState([]);
  const [carregandoServicosExecutados, setCarregandoServicosExecutados] = useState(false);
  const [forceUpdateServicos, setForceUpdateServicos] = useState(0);
  
  // Estados para validação de datas
  const [erroDataFinal, setErroDataFinal] = useState(false);
  const [mensagemErroData, setMensagemErroData] = useState('');
  
  // Estados para dados
  const [estatisticas, setEstatisticas] = useState(null);
  const [metricas, setMetricas] = useState(null);
  const [servicos, setServicos] = useState([]);
  const [carregandoEstatisticas, setCarregandoEstatisticas] = useState(true);
  const [carregandoMetricas, setCarregandoMetricas] = useState(true);
  const [carregandoServicos, setCarregandoServicos] = useState(true);
  const [gerandoPdf, setGerandoPdf] = useState(false);
  const [erro, setErro] = useState(null);
  const [sucessoMsg, setSucessoMsg] = useState(null);

  // Verificar se o usuário é admin
  const isAdmin = user?.cargo === 'ADMIN' || user?.cargo === 'Admin' || user?.cargo === 'admin';
  
  // Verificar se o usuário é atendente ou recepção
  const isAtendente = user?.cargo === 'ATENDENTE' || user?.cargo === 'Atendente' || user?.cargo === 'atendente';
  const isRecepcao = user?.cargo === 'RECEPCAO' || user?.cargo === 'Recepção' || user?.cargo === 'recepcao' || user?.cargo === 'Recepcao';
  
  // Usuários com acesso limitado (apenas desempenho dos atendentes)
  const hasLimitedAccess = isAtendente || isRecepcao;

  // Tema personalizado
  const customTheme = createTheme({
    ...baseTheme,
    typography: {
      fontFamily: [
        'Montserrat',
        '-apple-system',
        'BlinkMacSystemFont',
        '"Segoe UI"',
        'Roboto',
        '"Helvetica Neue"',
        'Arial',
        'sans-serif',
        '"Apple Color Emoji"',
        '"Segoe UI Emoji"',
        '"Segoe UI Symbol"',
      ].join(','),
    },
  });

  // ===================================================================
  // VALIDAÇÃO DE DATAS
  // ===================================================================
  
  // Função para validar se data inicial é maior que data final
  const validarDatas = (novaDataInicio, novaDataFim) => {
    if (novaDataInicio && novaDataFim && novaDataInicio.isAfter(novaDataFim)) {
      setErroDataFinal(true);
      setMensagemErroData('A data inicial não pode ser maior que a data final');
      return false;
    } else {
      setErroDataFinal(false);
      setMensagemErroData('');
      return true;
    }
  };

  // Função para lidar com mudança da data inicial
  const handleDataInicioChange = (e) => {
    const novaDataInicio = dayjs(e.target.value);
    setDataInicio(novaDataInicio);
    
    // Validar com a data final atual
    if (!validarDatas(novaDataInicio, dataFim)) {
      // Se a data inicial for maior que a final, ajustar a data final para ser igual à inicial
      setDataFim(novaDataInicio);
      // Mostrar mensagem temporária
      setTimeout(() => {
        setErroDataFinal(false);
        setMensagemErroData('');
      }, 3000);
    }
  };

  // Função para lidar com mudança da data final
  const handleDataFimChange = (e) => {
    const novaDataFim = dayjs(e.target.value);
    
    // Validar antes de definir
    if (validarDatas(dataInicio, novaDataFim)) {
      setDataFim(novaDataFim);
    } else {
      // Mostrar erro por alguns segundos e não alterar a data
      setTimeout(() => {
        setErroDataFinal(false);
        setMensagemErroData('');
      }, 3000);
    }
  };

  // Função otimizada para buscar todos os dados necessários em uma única chamada
  const buscarDadosRelatorio = async () => {
    try {
      setCarregandoEstatisticas(true);
      setCarregandoMetricas(true);
      setErro(null);
      
      const filtros = {
        dataInicio: dataInicio.format('YYYY-MM-DD'),
        dataFim: dataFim.format('YYYY-MM-DD')
      };
      
      console.log('[DEBUG] Relatorios - Buscando dados otimizados com filtros:', filtros);
      
      // Uma única chamada para o endpoint otimizado do backend
      const response = await relatoriosApi.buscarEstatisticasApi(filtros);
      
      if (response.success) {
        const dados = response.data;
        console.log('[DEBUG] Relatorios - Dados recebidos:', dados);
        
        // Usar os mesmos dados para estatísticas e métricas
        setEstatisticas(dados);
        setMetricas({
          senhasEmitidas: dados.senhasEmitidas || 0,
          senhasFinalizadas: dados.senhasFinalizadas || 0,
          senhasCanceladas: dados.senhasCanceladas || 0,
          tempoMedioAtendimento: dados.tempoMedioAtendimento || '0 min'
        });
        
        console.log('[DEBUG] Relatorios - Métricas dos cards configuradas:', {
          senhasEmitidas: dados.senhasEmitidas,
          senhasFinalizadas: dados.senhasFinalizadas,
          senhasCanceladas: dados.senhasCanceladas,
          tempoMedioAtendimento: dados.tempoMedioAtendimento
        });
      } else {
        setErro(response.message || 'Erro ao buscar dados do relatório');
      }
    } catch (error) {
      console.error('[DEBUG] Relatorios - Erro ao buscar dados:', error);
      setErro('Erro ao carregar dados do servidor');
    } finally {
      setCarregandoEstatisticas(false);
      setCarregandoMetricas(false);
    }
  };

  // Função para buscar serviços (mantida separada pois é usada para filtros)
  const buscarServicos = async () => {
    try {
      setCarregandoServicos(true);
      
      const response = await relatoriosApi.buscarServicos();
      
      if (response.success) {
        setServicos(response.data || []);
      } else {
        console.warn('Erro ao buscar serviços:', response.message);
        setServicos([]);
      }
    } catch (error) {
      console.error('Erro ao buscar serviços:', error);
      setServicos([]);
    } finally {
      setCarregandoServicos(false);
    }
  };

  // Função para buscar dados detalhados dos usuários
  const buscarDadosUsuariosDetalhados = async () => {
    try {
      setCarregandoUsuariosDetalhados(true);
      setErro(null);
      
      const filtros = {
        dataInicio: dataInicio.format('YYYY-MM-DD'),
        dataFim: dataFim.format('YYYY-MM-DD')
      };
      
      console.log('[DEBUG] Relatorios - Buscando dados detalhados de usuários com filtros:', filtros);
      
      const response = await relatoriosApi.buscarDadosUsuariosDetalhados(filtros);
      
      if (response.success) {
        setDadosUsuariosDetalhados(response.data || []);
        console.log('[DEBUG] Relatorios - Dados detalhados de usuários recebidos:', response.data);
      } else {
        setErro(response.message || 'Erro ao buscar dados detalhados dos usuários');
        setDadosUsuariosDetalhados([]);
      }
    } catch (error) {
      console.error('[DEBUG] Relatorios - Erro ao buscar dados detalhados dos usuários:', error);
      setErro('Erro ao carregar dados detalhados dos usuários');
      setDadosUsuariosDetalhados([]);
    } finally {
      setCarregandoUsuariosDetalhados(false);
    }
  };

  // Função para buscar dados detalhados das senhas
  const buscarDadosSenhasDetalhadas = async () => {
    try {
      setCarregandoSenhasDetalhadas(true);
      setErro(null);
      
      const filtros = {
        dataInicio: dataInicio.format('YYYY-MM-DD'),
        dataFim: dataFim.format('YYYY-MM-DD')
      };
      
      console.log('[DEBUG] Relatorios - Buscando dados detalhados de senhas com filtros:', filtros);
      
      const response = await relatoriosApi.buscarDadosDetalhados(filtros);
      
      if (response.success) {
        setDadosSenhasDetalhadas(response.data || []);
        console.log('[DEBUG] Relatorios - Dados detalhados de senhas recebidos:', response.data);
      } else {
        setErro(response.message || 'Erro ao buscar dados detalhados das senhas');
        setDadosSenhasDetalhadas([]);
      }
    } catch (error) {
      console.error('[DEBUG] Relatorios - Erro ao buscar dados detalhados das senhas:', error);
      setErro('Erro ao carregar dados detalhados das senhas');
      setDadosSenhasDetalhadas([]);
    } finally {
      setCarregandoSenhasDetalhadas(false);
    }
  };

  // Função para buscar dados dos serviços executados
  const buscarDadosServicosExecutados = async () => {
    try {
      setCarregandoServicosExecutados(true);
      setErro(null);
      
      const filtros = {
        dataInicio: dataInicio.format('YYYY-MM-DD'),
        dataFim: dataFim.format('YYYY-MM-DD')
      };
      
      console.log('[DEBUG] Relatorios - Buscando dados de serviços executados com filtros:', filtros);
      
      const response = await relatoriosApi.buscarDadosServicosExecutados(filtros);
      
      if (response.success) {
        setDadosServicosExecutados(response.data || []);
        console.log('[DEBUG] Relatorios - Dados de serviços executados recebidos:', response.data);
      } else {
        setErro(response.message || 'Erro ao buscar dados dos serviços executados');
        setDadosServicosExecutados([]);
      }
    } catch (error) {
      console.error('[DEBUG] Relatorios - Erro ao buscar dados dos serviços executados:', error);
      setErro('Erro ao carregar dados dos serviços executados');
      setDadosServicosExecutados([]);
    } finally {
      setCarregandoServicosExecutados(false);
    }
  };

  // Função para lidar com a mudança de abas
  const handleTabChange = (event, newValue) => {
    setActiveTab(newValue);
    
    // Se mudou para a aba de desempenho detalhado e ainda não carregou os dados
    if (newValue === 1 && dadosUsuariosDetalhados.length === 0) {
      buscarDadosUsuariosDetalhados();
    }
    
    // Se mudou para a aba de senhas detalhadas e ainda não carregou os dados
    if (newValue === 2 && dadosSenhasDetalhadas.length === 0) {
      buscarDadosSenhasDetalhadas();
    }
    
    // Se mudou para a aba de serviços executados e ainda não carregou os dados
    if (newValue === 3 && dadosServicosExecutados.length === 0) {
      buscarDadosServicosExecutados();
    }
  };

  // Função para atualizar dados - simplificada
  const atualizarDados = () => {
    if (hasLimitedAccess) {
      // Para usuários com acesso limitado, só carregaremos dados de atendentes
      return;
    }
    
    buscarDadosRelatorio();
    buscarServicos();
    
    // Se estiver na aba de Desempenho Detalhado, atualizar também esses dados
    if (activeTab === 1) {
      buscarDadosUsuariosDetalhados();
    }
    
    // Se estiver na aba de Senhas Detalhadas, atualizar também esses dados
    if (activeTab === 2) {
      buscarDadosSenhasDetalhadas();
    }
    
    // Se estiver na aba de Serviços Executados, forçar atualização
    if (activeTab === 3) {
      setForceUpdateServicos(prev => prev + 1);
    }
  };

  // Carregar dados ao montar o componente
  useEffect(() => {
    if (!hasLimitedAccess) {
      atualizarDados();
    }
  }, [hasLimitedAccess]);

  // Atualizar dados quando os filtros de data mudarem
  useEffect(() => {
    if (dataInicio && dataFim && !hasLimitedAccess) {
      buscarDadosRelatorio();
      
      // Se a aba de usuários detalhados estiver ativa, atualizar seus dados também
      if (activeTab === 1) {
        buscarDadosUsuariosDetalhados();
      }
      
      // Se a aba de senhas detalhadas estiver ativa, atualizar seus dados também
      if (activeTab === 2) {
        buscarDadosSenhasDetalhadas();
      }
      
      // Se a aba de serviços executados estiver ativa, forçar atualização
      if (activeTab === 3) {
        setForceUpdateServicos(prev => prev + 1);
      }
    }
  }, [dataInicio, dataFim, hasLimitedAccess, activeTab]);

  // Função para exportar dados
  const exportarDados = async (formato) => {
    if (hasLimitedAccess) {
      setErro('Você não tem permissão para exportar relatórios completos');
      return;
    }

    if (formato === 'pdf') {
      setErro(null);
      setSucessoMsg(null);
      setGerandoPdf(true);
      
      try {
        // Validar datas
        if (!dataInicio || !dataFim || !dayjs(dataInicio).isValid() || !dayjs(dataFim).isValid()) {
          throw new Error('Período de datas inválido para o relatório.');
        }
        
        // Validar métricas
        if (!metricas || !Array.isArray(metricasPrincipais) || metricasPrincipais.length === 0) {
          console.warn('Métricas não disponíveis para o relatório, tentando gerar com dados disponíveis');
        }
        
        // Opções para geração do PDF
        const opcoesGeracaoPDF = {
          tipoRelatorio: activeTab === 1 ? 'usuarios-detalhado' : activeTab === 2 ? 'senhas-detalhado' : activeTab === 3 ? 'servicos-executados' : (tipoRelatorio || 'geral'),
          dataInicio,
          dataFim,
          metricasPrincipais,
          onSuccess: (mensagem) => {
            setSucessoMsg(mensagem);
            setTimeout(() => setSucessoMsg(null), 5000);
            setGerandoPdf(false);
          },
          onError: (mensagem) => {
            setErro(mensagem);
            setGerandoPdf(false);
          }
        };
        
        // Gerar o PDF usando o PDFGenerator
        PDFGenerator.gerarPDF(opcoesGeracaoPDF);
      } catch (error) {
        console.error('Erro ao gerar PDF:', error);
        setErro(`Ocorreu um erro ao gerar o PDF: ${error.message}`);
        setGerandoPdf(false);
      }
    } else if (formato === 'excel') {
      try {
        setErro(null);
        setSucessoMsg(null);
        console.log('Iniciando exportação completa para Excel...');
        
        // Validar datas
        if (!dataInicio || !dataFim || !dayjs(dataInicio).isValid() || !dayjs(dataFim).isValid()) {
          throw new Error('Período de datas inválido para o relatório.');
        }
        
        // Preparar filtros para as APIs
        const filtros = {
          dataInicio: dataInicio.format('YYYY-MM-DD'),
          dataFim: dataFim.format('YYYY-MM-DD'),
          servicosSelecionados
        };
        
        // Buscar todos os dados necessários em paralelo
        console.log('Buscando dados de todos os relatórios...');
        const [
          dadosEstatisticas,
          dadosMovimentoHorario,
          dadosServicos,
          dadosUsuarios,
          dadosSenhasDetalhadas,
          dadosServicosExecutados,
          dadosAtendentes
        ] = await Promise.all([
          relatoriosApi.buscarEstatisticasApi(filtros),
          relatoriosApi.buscarMovimentoPorHorario(filtros),
          relatoriosApi.buscarDadosPorServico(filtros),
          relatoriosApi.buscarRelatorioUsuariosDetalhado(filtros),
          relatoriosApi.buscarDadosDetalhados(filtros),
          relatoriosApi.buscarDadosServicosExecutados(filtros),
          relatoriosApi.buscarDadosAtendentes(filtros)
        ]);
        
        console.log('Dados obtidos:', {
          estatisticas: dadosEstatisticas,
          movimento: dadosMovimentoHorario,
          servicos: dadosServicos,
          usuarios: dadosUsuarios,
          senhas: dadosSenhasDetalhadas,
          servicosExec: dadosServicosExecutados,
          atendentes: dadosAtendentes
        });
        
        // Criar workbook
        const workbook = XLSX.utils.book_new();
        
        // Usar senhas não compareceu (canceladas) da API
        const senhasNaoCompareceu = dadosEstatisticas.data?.senhasNaoCompareceu || dadosEstatisticas.data?.senhasCanceladas || 0;
        
        // Extrair tempos médios mantendo formato de minutos e segundos
        const formatarTempo = (valor) => {
          if (!valor) return '0 min 0 seg';
          if (typeof valor === 'string') {
            // Se já está formatado, retornar como está
            if (valor.includes('min') || valor.includes('seg')) {
              return valor;
            }
            // Se é apenas número + "min", adicionar segundos
            if (valor.includes('min') && !valor.includes('seg')) {
              return valor.replace('min', 'min 0 seg');
            }
          }
          return String(valor);
        };
        
        const tempoMedioAtendimento = formatarTempo(dadosEstatisticas.data?.tempoMedioAtendimento);
        const tempoMedioEspera = formatarTempo(dadosEstatisticas.data?.tempoMedioEspera);
        
        const totalSenhas = dadosEstatisticas.data?.totalSenhas || dadosEstatisticas.data?.senhasEmitidas || 0;
        const senhasFinalizadas = dadosEstatisticas.data?.senhasFinalizadas || dadosEstatisticas.data?.senhasConcluidas || 0;
        
        // ABA 1: Resumo Geral
        const resumoGeral = [
          ['RELATÓRIO GERAL DO SISTEMA'],
          ['Período:', `${dataInicio.format('DD/MM/YYYY')} até ${dataFim.format('DD/MM/YYYY')}`],
          ['Gerado em:', dayjs().format('DD/MM/YYYY HH:mm:ss')],
          [''],
          ['MÉTRICAS PRINCIPAIS'],
          ['Total de Senhas', totalSenhas],
          ['Senhas Finalizadas', senhasFinalizadas],
          ['Senhas Não Compareceu', senhasNaoCompareceu],
          ['Senhas Aguardando', dadosEstatisticas.data?.senhasAguardando || 0],
          ['Senhas Chamando', dadosEstatisticas.data?.senhasChamando || 0],
          ['Tempo Médio de Atendimento', tempoMedioAtendimento],
          ['Tempo Médio de Espera', tempoMedioEspera],
          ['Senhas Preferenciais Emitidas', dadosEstatisticas.data?.atendimentosPreferenciais || 0],
          ['Taxa de Conclusão (%)', totalSenhas > 0 ? 
            ((senhasFinalizadas / totalSenhas) * 100).toFixed(2) : '0.00'],
          ['Taxa de Não Comparecimento (%)', totalSenhas > 0 ? 
            ((senhasNaoCompareceu / totalSenhas) * 100).toFixed(2) : '0.00'],
          [''],
          ['DISTRIBUIÇÃO POR HORÁRIO'],
          ['Horário', 'Quantidade de Senhas']
        ];
        
        // Adicionar dados de movimento por horário e calcular total
        let totalPorHorario = 0;
        if (dadosMovimentoHorario.success && dadosMovimentoHorario.data?.movimentoPorHorario) {
          dadosMovimentoHorario.data.movimentoPorHorario.forEach(item => {
            resumoGeral.push([item.hora, item.quantidade]);
            totalPorHorario += item.quantidade;
          });
          // Adicionar linha de total
          resumoGeral.push(['TOTAL', totalPorHorario]);
        }
        
        const wsResumo = XLSX.utils.aoa_to_sheet(resumoGeral);
        // Configurar larguras das colunas
        wsResumo['!cols'] = [{ wch: 30 }, { wch: 20 }];
        XLSX.utils.book_append_sheet(workbook, wsResumo, 'Resumo Geral');
        
        // ABA 2: Desempenho Detalhado por Usuário
        if (dadosUsuarios.success && dadosUsuarios.data?.length > 0) {
          const dadosUsuariosFormatados = [
            ['DESEMPENHO DETALHADO POR USUÁRIO'],
            [''],
            ['Nome', 'Total Atendimentos', 'TME (Espera)', 'TMD (Deslocamento)', 'TMA (Atendimento)', 'Tempo Total']
          ];
          
          dadosUsuarios.data.forEach(usuario => {
            // Determinar serviço mais atendido a partir dos dados de atendentes se disponível
            let servicoMaisAtendido = 'N/A';
            if (dadosAtendentes.success && dadosAtendentes.data?.atendentes?.length > 0) {
              const atendenteInfo = dadosAtendentes.data.atendentes.find(a => a.nome === usuario.nome);
              servicoMaisAtendido = atendenteInfo?.servicoMaisAtendido || 'N/A';
            }
            
            dadosUsuariosFormatados.push([
              usuario.nome || 'N/A',
              usuario.atendimentos || usuario.totalAtendimentos || 0,
              usuario.tme || 'N/A',
              usuario.tmd || 'N/A', 
              usuario.tma || 'N/A',
              usuario.tempoTotal || 'N/A',
            ]);
          });
          
          const wsUsuarios = XLSX.utils.aoa_to_sheet(dadosUsuariosFormatados);
          // Configurar larguras das colunas
          wsUsuarios['!cols'] = [
            { wch: 25 }, { wch: 15 }, { wch: 15 }, { wch: 15 }, 
            { wch: 15 }, { wch: 15 }, { wch: 30 }
          ];
          XLSX.utils.book_append_sheet(workbook, wsUsuarios, 'Desempenho Usuários');
        }
        
        // ABA 3: Senhas Emitidas
        if (dadosServicos.success && dadosServicos.data?.length > 0) {
          const dadosServicosFormatados = [
            ['SENHAS EMITIDAS POR SERVIÇO'],
            [''],
            ['Serviço', 'Quantidade', 'Percentual (%)', 'Tempo Médio Atendimento', 'Tempo Médio Espera']
          ];
          
          // Função para converter minutos em formato "X min Y seg"
          const formatarTempoMinSeg = (minutos) => {
            if (!minutos || minutos === 0) return '0 min 0 seg';
            const mins = Math.floor(minutos);
            const segs = Math.round((minutos - mins) * 60);
            return `${mins} min ${segs} seg`;
          };
          
          let totalQuantidade = 0;
          let totalPercentual = 0;
          
          dadosServicos.data.forEach(servico => {
            const quantidade = servico.quantidade || 0;
            const percentual = parseFloat(servico.percentual?.toFixed(2) || '0.00');
            
            totalQuantidade += quantidade;
            totalPercentual += percentual;
            
            dadosServicosFormatados.push([
              servico.servico || 'N/A',
              quantidade,
              percentual.toFixed(2),
              formatarTempoMinSeg(servico.tempoMedioAtendimento || 0),
              formatarTempoMinSeg(servico.tempoMedioEspera || 0)
            ]);
          });
          
          // Adicionar linha de total
          dadosServicosFormatados.push([
            '',
            '',
            '',
            '',
            ''
          ]);
          dadosServicosFormatados.push([
            'TOTAL',
            totalQuantidade,
            totalPercentual.toFixed(2),
            '-',
            '-'
          ]);
          
          const wsServicos = XLSX.utils.aoa_to_sheet(dadosServicosFormatados);
          // Configurar larguras das colunas
          wsServicos['!cols'] = [
            { wch: 35 }, { wch: 12 }, { wch: 15 }, { wch: 25 }, { wch: 25 }
          ];
          XLSX.utils.book_append_sheet(workbook, wsServicos, 'Senhas Emitidas');
        }
        
        // ABA 4: Senhas Detalhadas
        if (dadosSenhasDetalhadas.success && dadosSenhasDetalhadas.data?.length > 0) {
          const senhasDetalhadas = [
            ['SENHAS DETALHADAS'],
            [''],
            ['Nº', 'Senha', 'Data/Hora Emissão', 'Data/Hora Chamada', 'Data/Hora Início', 'Data/Hora Finalização', 
             'Tempo Atendimento', 'Serviço', 'Tipo', 'Status', 'Atendente', 'Guichê', 'Serviços Adicionais', 
             'Serviço Alterado', 'Motivo Emissão', 'Usuario Emitiu']
          ];
          
          dadosSenhasDetalhadas.data.forEach((senha, index) => {
            const formatarDataHora = (data) => {
              if (!data) return '-';
              return dayjs(data).format('DD/MM/YYYY HH:mm:ss');
            };
            
            const calcularTempoAtendimento = (inicio, fim) => {
              if (!inicio || !fim) return '-';
              const diffMs = dayjs(fim).diff(dayjs(inicio));
              const minutes = Math.floor(diffMs / 60000);
              const seconds = Math.floor((diffMs % 60000) / 1000);
              return `${minutes}m ${seconds}s`;
            };
            
            const traduzirStatus = (status) => {
              const statusMap = {
                'AGUARDANDO': 'Aguardando',
                'CHAMADA': 'Chamada',
                'EM_ATENDIMENTO': 'Em Atendimento',
                'FINALIZADA': 'Finalizada',
                'CANCELADA': 'Cancelada',
                'NAO_COMPARECEU': 'Não Compareceu',
                'REABERTA': 'Reaberta'
              };
              return statusMap[status] || status;
            };
            
            // Formatar serviços adicionais
            let servicosAdicionais = '-';
            if (senha.servicos_adicionais && senha.servicos_adicionais.length > 0) {
              if (Array.isArray(senha.servicos_adicionais)) {
                servicosAdicionais = senha.servicos_adicionais.join(', ');
              } else {
                servicosAdicionais = senha.servicos_adicionais;
              }
            }
            
            senhasDetalhadas.push([
              index + 1,
              senha.numero || '-',
              formatarDataHora(senha.emitida_em),
              formatarDataHora(senha.chamada_em),
              formatarDataHora(senha.inicio_atendimento || senha.iniciada_em),
              formatarDataHora(senha.finalizada_em),
              calcularTempoAtendimento(senha.inicio_atendimento || senha.iniciada_em, senha.finalizada_em),
              senha.servico_nome || '-',
              senha.preferencial ? 'Preferencial' : 'Comum',
              traduzirStatus(senha.status),
              senha.usuario_nome || '-',
              senha.guiche || '-',
              servicosAdicionais,
              senha.servico_alterado ? 'Sim' : 'Não',
              senha.motivo_emissao || '-',
              senha.usuario_emitiu || '-'
            ]);
          });
          
          const wsSenhas = XLSX.utils.aoa_to_sheet(senhasDetalhadas);
          // Configurar larguras das colunas
          wsSenhas['!cols'] = [
            { wch: 5 }, { wch: 10 }, { wch: 18 }, { wch: 18 }, { wch: 18 }, { wch: 18 },
            { wch: 15 }, { wch: 25 }, { wch: 12 }, { wch: 12 }, { wch: 20 }, { wch: 8 },
            { wch: 30 }, { wch: 12 }, { wch: 20 }, { wch: 20 }
          ];
          XLSX.utils.book_append_sheet(workbook, wsSenhas, 'Senhas Detalhadas');
        }
        
        // ABA 5: Serviços Executados
        if (dadosServicosExecutados.success && dadosServicosExecutados.data?.length > 0) {
          const servicosExecutados = [
            ['SERVIÇOS EXECUTADOS'],
            [''],
            ['Serviço', 'Status', 'Senhas Finalizadas', 'Alterações de Serviço']
          ];
          
          let totalFinalizadas = 0;
          let totalAlteracoes = 0;
          
          dadosServicosExecutados.data.forEach(item => {
            const senhasFinalizadas = item.senhas_finalizadas || 0;
            const alteracoes = item.alteracoes_servico || 0;
            
            totalFinalizadas += senhasFinalizadas;
            totalAlteracoes += alteracoes;
            
            servicosExecutados.push([
              item.nome || 'N/A',
              item.ativo ? 'Ativo' : 'Inativo',
              senhasFinalizadas,
              alteracoes
            ]);
          });
          
          // Adicionar linha de total
          servicosExecutados.push([
            '',
            '',
            '',
            ''
          ]);
          servicosExecutados.push([
            'TOTAL',
            '-',
            totalFinalizadas,
            totalAlteracoes
          ]);
          
          const wsServicosExec = XLSX.utils.aoa_to_sheet(servicosExecutados);
          // Configurar larguras das colunas
          wsServicosExec['!cols'] = [
            { wch: 35 }, { wch: 12 }, { wch: 18 }, { wch: 20 }
          ];
          XLSX.utils.book_append_sheet(workbook, wsServicosExec, 'Serviços Executados');
        }
        
        // Gerar nome do arquivo
        const agora = new Date();
        const dataFormatada = agora.toLocaleDateString('pt-BR').replace(/\//g, '-');
        const horaFormatada = agora.toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' }).replace(/:/g, '-');
        const nomeArquivo = `relatorio-completo_${dataFormatada}_${horaFormatada}.xlsx`;
        
        // Fazer download do arquivo
        XLSX.writeFile(workbook, nomeArquivo);
        
        console.log(`Exportação Excel concluída: ${nomeArquivo}`);
        setSucessoMsg(`✅ Relatório Excel gerado com sucesso! Arquivo: ${nomeArquivo}`);
        setTimeout(() => setSucessoMsg(null), 5000);
        
      } catch (error) {
        console.error('Erro ao exportar Excel:', error);
        setErro(`Erro ao gerar relatório Excel: ${error.message}`);
      }
    }
  };

  // Métricas principais baseadas nos dados reais filtrados (apenas para admin)
  const metricasPrincipais = !hasLimitedAccess && metricas ? [
    {
      titulo: 'Senhas Emitidas',
      valor: metricas.senhasEmitidas,
      icon: <GroupIcon />,
      cor: customTheme.palette.primary.main,
      formato: 'numero'
    },
    {
      titulo: 'Senhas Finalizadas',
      valor: metricas.senhasFinalizadas,
      icon: <TrendingUpIcon />,
      cor: '#4caf50',
      formato: 'numero'
    },
    {
      titulo: 'Tempo Médio de Atendimento',
      valor: metricas.tempoMedioAtendimento,
      icon: <ScheduleIcon />,
      cor: '#ff9800',
      formato: 'tempo'
    },
    {
      titulo: 'Senhas Canceladas',
      valor: metricas.senhasCanceladas,
      icon: <AccessTimeIcon />,
      cor: '#f44336',
      formato: 'numero'
    }
  ] : [];

  return (
    <Box sx={{ display: 'flex', minHeight: '100vh', bgcolor: alpha(customTheme.palette.primary.main, 0.03) }}>
      <Sidebar sidebarOpen={sidebarOpen} customTheme={customTheme} />

      <Box sx={{ flexGrow: 1, display: 'flex', flexDirection: 'column' }}>
        <Header
          sidebarOpen={sidebarOpen}
                      handleSidebarToggle={toggleSidebar}
          customTheme={customTheme}
        />

        <Container maxWidth="xxl" sx={{ mt: 10, mb: 4 }}>
          {/* Breadcrumbs */}
          <Breadcrumbs 
            separator={<NavigateNextIcon fontSize="small" />} 
            aria-label="breadcrumb"
            sx={{ mb: 3 }}
          >
            <Link 
              color="inherit" 
              href="/dashboard" 
              sx={{ 
                display: 'flex', 
                alignItems: 'center',
                textDecoration: 'none',
                color: 'text.primary',
                '&:hover': { textDecoration: 'underline' }
              }}
            >
              <HomeIcon sx={{ mr: 0.5 }} fontSize="inherit" />
              Dashboard
            </Link>
            <Typography color="text.primary" sx={{ display: 'flex', alignItems: 'center' }}>
              <AssessmentIcon sx={{ mr: 0.5 }} fontSize="inherit" />
              {hasLimitedAccess ? 'Desempenho dos Atendentes' : 'Relatórios'}
            </Typography>
          </Breadcrumbs>
          
          <Paper
            elevation={0}
            sx={{
              p: 4,
              borderRadius: 4,
              border: `1px solid ${alpha(customTheme.palette.primary.main, 0.2)}`,
              background: isDarkMode
                ? `linear-gradient(135deg, ${alpha(customTheme.palette.grey[900], 0.95)} 0%, ${alpha(customTheme.palette.grey[800], 0.9)} 100%)`
                : 'background.paper',
              backdropFilter: 'blur(10px)',
            }}
          >
            {/* Cabeçalho */}
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 4 }}>
              <Box>
                <Typography
                  variant="h4"
                  component="h1"
                  sx={{
                    fontWeight: 700,
                    mb: 1,
                    background: isDarkMode
                      ? `linear-gradient(90deg, ${customTheme.palette.primary.light} 0%, ${customTheme.palette.primary.main} 100%)`
                      : `linear-gradient(90deg, ${customTheme.palette.primary.main} 0%, ${customTheme.palette.primary.dark} 100%)`,
                    WebkitBackgroundClip: 'text',
                    WebkitTextFillColor: 'transparent',
                  }}
                >
                  {hasLimitedAccess ? 'Desempenho dos Atendentes' : 'Relatórios do Sistema'}
                </Typography>
                <Typography variant="body1" color="text.secondary">
                  {hasLimitedAccess 
                    ? 'Análise do desempenho e estatísticas dos atendentes'
                    : 'Análise detalhada do desempenho e estatísticas do atendimento'
                  }
                </Typography>
              </Box>
              
              {!hasLimitedAccess && (
                <Stack direction="row" spacing={2}>
                  <Tooltip title="Atualizar dados">
                    <IconButton 
                      onClick={atualizarDados}
                      disabled={carregandoEstatisticas || carregandoMetricas || carregandoUsuariosDetalhados || carregandoSenhasDetalhadas || carregandoServicosExecutados}
                      sx={{ color: customTheme.palette.primary.main }}
                    >
                      {(carregandoEstatisticas || carregandoMetricas || carregandoUsuariosDetalhados || carregandoSenhasDetalhadas || carregandoServicosExecutados) ? <CircularProgress size={20} /> : <RefreshIcon />}
                    </IconButton>
                  </Tooltip>
                  <Button
                    variant="outlined"
                    startIcon={gerandoPdf ? <CircularProgress size={18} /> : <PrintIcon />}
                    onClick={() => exportarDados('pdf')}
                    sx={{ borderColor: customTheme.palette.primary.main }}
                    disabled={carregandoEstatisticas || carregandoMetricas || carregandoUsuariosDetalhados || carregandoSenhasDetalhadas || carregandoServicosExecutados || gerandoPdf}
                  >
                    {gerandoPdf ? 'Gerando PDF...' : 'Exportar PDF'}
                  </Button>
                  <Button
                    variant="contained"
                    startIcon={<FileDownloadIcon />}
                    onClick={() => exportarDados('excel')}
                    sx={{ bgcolor: customTheme.palette.primary.main }}
                    disabled={carregandoEstatisticas || carregandoMetricas || carregandoUsuariosDetalhados || carregandoSenhasDetalhadas || carregandoServicosExecutados}
                  >
                    Exportar Excel
                  </Button>
                </Stack>
              )}
            </Box>

            {/* Exibir erro se houver */}
            {erro && (
              <Alert severity="error" sx={{ mb: 3 }}>
                {erro}
              </Alert>
            )}

            {/* Exibir mensagem de sucesso */}
            {sucessoMsg && (
              <Alert severity="success" sx={{ mb: 3 }}>
                {sucessoMsg}
              </Alert>
            )}

            {/* Filtros - apenas para admin ou se aplicável */}
            {!hasLimitedAccess && (
              <Grid container spacing={3} alignItems="center">
                <Grid item xs={12} sm={6} md={3}>
                  <TextField
                    label="Data Início"
                    type="date"
                    value={dataInicio.format('YYYY-MM-DD')}
                    onChange={handleDataInicioChange}
                    fullWidth
                    size="small"
                    InputLabelProps={{ shrink: true }}
                    sx={{
                      '& input[type="date"]::-webkit-calendar-picker-indicator': {
                        filter: isDarkMode ? 'invert(1)' : 'none',
                        cursor: 'pointer',
                      },
                      '& input[type="date"]::-webkit-calendar-picker-indicator:hover': {
                        filter: isDarkMode ? 'invert(1) brightness(1.2)' : 'brightness(0.8)',
                      }
                    }}
                  />
                </Grid>
                
                <Grid item xs={12} sm={6} md={3}>
                  <TextField
                    label="Data Fim"
                    type="date"
                    value={dataFim.format('YYYY-MM-DD')}
                    onChange={handleDataFimChange}
                    fullWidth
                    size="small"
                    InputLabelProps={{ shrink: true }}
                    error={erroDataFinal}
                    helperText={erroDataFinal ? mensagemErroData : ''}
                    FormHelperTextProps={{
                      sx: {
                        backgroundColor: 'rgba(244, 67, 54, 0.1)',
                        padding: '4px 8px',
                        borderRadius: '4px',
                        margin: '4px 0 0 0',
                        fontSize: '0.75rem'
                      }
                    }}
                    sx={{
                      '& input[type="date"]::-webkit-calendar-picker-indicator': {
                        filter: isDarkMode ? 'invert(1)' : 'none',
                        cursor: 'pointer',
                      },
                      '& input[type="date"]::-webkit-calendar-picker-indicator:hover': {
                        filter: isDarkMode ? 'invert(1) brightness(1.2)' : 'brightness(0.8)',
                      }
                    }}
                  />
                </Grid>
              </Grid>
            )}

            {/* Filtros simplificados para usuários com acesso limitado */}
            {hasLimitedAccess && (
              <Grid container spacing={3} alignItems="center" sx={{ mb: 3 }}>
                <Grid item xs={12} sm={6} md={3}>
                  <TextField
                    label="Data Início"
                    type="date"
                    value={dataInicio.format('YYYY-MM-DD')}
                    onChange={handleDataInicioChange}
                    fullWidth
                    size="small"
                    InputLabelProps={{ shrink: true }}
                    sx={{
                      '& input[type="date"]::-webkit-calendar-picker-indicator': {
                        filter: isDarkMode ? 'invert(1)' : 'none',
                        cursor: 'pointer',
                      },
                      '& input[type="date"]::-webkit-calendar-picker-indicator:hover': {
                        filter: isDarkMode ? 'invert(1) brightness(1.2)' : 'brightness(0.8)',
                      }
                    }}
                  />
                </Grid>
                
                <Grid item xs={12} sm={6} md={3}>
                  <TextField
                    label="Data Fim"
                    type="date"
                    value={dataFim.format('YYYY-MM-DD')}
                    onChange={handleDataFimChange}
                    fullWidth
                    size="small"
                    InputLabelProps={{ shrink: true }}
                    error={erroDataFinal}
                    helperText={erroDataFinal ? mensagemErroData : ''}
                    FormHelperTextProps={{
                      sx: {
                        backgroundColor: 'rgba(244, 67, 54, 0.1)',
                        padding: '4px 8px',
                        borderRadius: '4px',
                        margin: '4px 0 0 0',
                        fontSize: '0.75rem'
                      }
                    }}
                    sx={{
                      '& input[type="date"]::-webkit-calendar-picker-indicator': {
                        filter: isDarkMode ? 'invert(1)' : 'none',
                        cursor: 'pointer',
                      },
                      '& input[type="date"]::-webkit-calendar-picker-indicator:hover': {
                        filter: isDarkMode ? 'invert(1) brightness(1.2)' : 'brightness(0.8)',
                      }
                    }}
                  />
                </Grid>
              </Grid>
            )}

            {!hasLimitedAccess && activeTab === 0 && <Divider sx={{ my: 4 }} />}

            {/* Cards de Métricas - apenas para admin e apenas na aba de Relatórios Gerais */}
            {!hasLimitedAccess && activeTab === 0 && (
              <Grid container spacing={3} sx={{ mb: 4 }}>
                {metricasPrincipais.map((metrica, index) => (
                  <Grid item xs={12} sm={6} md={3} key={index}>
                    <MetricasCard
                      titulo={metrica.titulo}
                      valor={metrica.valor}
                      icon={metrica.icon}
                      cor={metrica.cor}
                      formato={metrica.formato}
                      carregando={carregandoMetricas}
                      customTheme={customTheme}
                    />
                  </Grid>
                ))}
              </Grid>
            )}

            {!hasLimitedAccess && (activeTab === 1 || activeTab === 2 || activeTab === 3) && <Box sx={{ my: 4 }} />}

            {/* Sistema de Abas - apenas para admin */}
            {!hasLimitedAccess && isAdmin && (
              <>
                <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 3 }}>
                  <Tabs 
                    value={activeTab} 
                    onChange={handleTabChange}
                    sx={{
                      '& .MuiTabs-indicator': {
                        backgroundColor: customTheme.palette.primary.main,
                        height: 3,
                        borderRadius: '3px 3px 0 0',
                      },
                      '& .MuiTab-root': {
                        textTransform: 'none',
                        fontWeight: 600,
                        fontSize: '1rem',
                        minHeight: 48,
                        '&.Mui-selected': {
                          color: customTheme.palette.primary.main,
                        },
                      },
                    }}
                  >
                    <Tab 
                      label="Relatórios Gerais" 
                      icon={<AssessmentIcon />}
                      iconPosition="start"
                    />
                    <Tab 
                      label="Desempenho Detalhado" 
                      icon={<PeopleIcon />}
                      iconPosition="start"
                    />
                    <Tab 
                      label="Senhas Detalhadas" 
                      icon={<TrendingUpIcon />}
                      iconPosition="start"
                    />
                    <Tab 
                      label="Serviços Executados" 
                      icon={<BusinessIcon />}
                      iconPosition="start"
                    />
                  </Tabs>
                </Box>

                {/* Conteúdo das Abas */}
                <Box id="relatorio-container">
                  {/* Aba 1: Relatórios Gerais */}
                  {activeTab === 0 && (
                    <>
                      {/* Relatórios Específicos */}
                      {tipoRelatorio === 'geral' && (
                        <RelatorioGeral 
                          dados={estatisticas} 
                          customTheme={customTheme}
                          carregando={carregandoEstatisticas}
                          filtros={{ dataInicio, dataFim, servicosSelecionados }}
                        />
                      )}
                      
                      {tipoRelatorio === 'servicos' && (
                        <RelatorioServicos 
                          dados={estatisticas} 
                          customTheme={customTheme}
                          carregando={carregandoEstatisticas}
                          filtros={{ dataInicio, dataFim, servicosSelecionados }}
                        />
                      )}
                      
                      {tipoRelatorio === 'tempo' && (
                        <RelatorioTempoEspera 
                          dados={estatisticas} 
                          customTheme={customTheme}
                          carregando={carregandoEstatisticas}
                          filtros={{ dataInicio, dataFim, servicosSelecionados }}
                        />
                      )}
                      
                      {tipoRelatorio === 'detalhado' && (
                        <TabelaDetalhada 
                          dados={estatisticas} 
                          customTheme={customTheme}
                          carregando={carregandoEstatisticas}
                          filtros={{ dataInicio, dataFim, servicosSelecionados }}
                        />
                      )}
                    </>
                  )}

                  {/* Aba 2: Desempenho Detalhado */}
                  {activeTab === 1 && (
                    <Box>
                      <Typography variant="h6" sx={{ mb: 2, fontWeight: 600 }}>
                        Tabela de Desempenho Detalhado por Usuário
                      </Typography>
                      <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                        Análise detalhada das métricas TME (Tempo Médio de Espera), TMD (Tempo Médio de Deslocamento), 
                        TMA (Tempo Médio de Atendimento) e Tempo Total para cada usuário.
                      </Typography>
                      
                      <TabelaDesempenhoUsuarios
                        dados={dadosUsuariosDetalhados}
                        carregando={carregandoUsuariosDetalhados}
                        customTheme={customTheme}
                        erro={erro}
                        filtros={{ 
                          dataInicio: dataInicio.format('YYYY-MM-DD'), 
                          dataFim: dataFim.format('YYYY-MM-DD') 
                        }}
                      />
                    </Box>
                  )}

                  {/* Aba 3: Senhas Detalhadas */}
                  {activeTab === 2 && (
                    <Box>
                      <Typography variant="h6" sx={{ mb: 2, fontWeight: 600 }}>
                        Relatório Completo de Senhas
                      </Typography>
                      <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                        Visualização detalhada de todas as senhas emitidas com informações completas de atendimento, 
                        incluindo datas e horários de emissão, chamada, início e finalização do atendimento.
                      </Typography>
                      
                      <TabelaSenhasDetalhadas
                        customTheme={customTheme}
                        carregando={carregandoSenhasDetalhadas}
                        filtros={{ 
                          dataInicio: dataInicio.format('YYYY-MM-DD'), 
                          dataFim: dataFim.format('YYYY-MM-DD') 
                        }}
                      />
                    </Box>
                  )}

                  {/* Aba 4: Serviços Executados */}
                  {activeTab === 3 && (
                    <Box>
                                             <RelatorioServicosExecutados
                         customTheme={customTheme}
                         carregando={carregandoServicosExecutados}
                         filtros={{ 
                           dataInicio: dataInicio.format('YYYY-MM-DD'), 
                           dataFim: dataFim.format('YYYY-MM-DD') 
                         }}
                         erro={erro}
                         forceUpdate={forceUpdateServicos}
                       />
                    </Box>
                  )}
                </Box>
              </>
            )}

            {/* Conteúdo para usuários com acesso limitado */}
            {hasLimitedAccess && (
              <Box id="relatorio-container">
                <RelatorioAtendentes 
                  dados={null} 
                  customTheme={customTheme}
                  carregando={false}
                  filtros={{ dataInicio, dataFim, servicosSelecionados }}
                  hasLimitedAccess={hasLimitedAccess}
                />
              </Box>
            )}

            {/* Conteúdo para admin não implementado ainda (fallback) */}
            {!hasLimitedAccess && !isAdmin && (
              <Box id="relatorio-container">
                <RelatorioGeral 
                  dados={estatisticas} 
                  customTheme={customTheme}
                  carregando={carregandoEstatisticas}
                  filtros={{ dataInicio, dataFim, servicosSelecionados }}
                />
              </Box>
            )}
          </Paper>
        </Container>
      </Box>
    </Box>
  );
};

export default Relatorios; 